
/**
 * Dynamically loads a Google Font into the document head.
 * Uses <link rel="preload"> to start fetching the font file immediately,
 * then injects the stylesheet. This prevents FOUT (Flash of Unstyled Text).
 * @param {string} fontFamily - The name of the font family (e.g., 'Bungee Spice').
 */
export const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || fontFamily === 'inherit' || fontFamily === 'sans-serif') return;

  // Normalize the font name for the unique element ID
  const normalizedName = fontFamily.replace(/\s+/g, '-').toLowerCase();
  const id = `font-${normalizedName}`;
  if (document.getElementById(id)) return;

  const encodedFont = fontFamily.replace(/\s+/g, '+');
  const href = `https://fonts.googleapis.com/css2?family=${encodedFont}:wght@400;700;900&display=swap`;

  // 1. Preconnect to Google Fonts domains (only once)
  if (!document.getElementById('gf-preconnect')) {
    const preconnect = document.createElement('link');
    preconnect.id = 'gf-preconnect';
    preconnect.rel = 'preconnect';
    preconnect.href = 'https://fonts.googleapis.com';
    document.head.appendChild(preconnect);

    const preconnectStatic = document.createElement('link');
    preconnectStatic.rel = 'preconnect';
    preconnectStatic.href = 'https://fonts.gstatic.com';
    preconnectStatic.crossOrigin = 'anonymous';
    document.head.appendChild(preconnectStatic);
  }

  // 2. Inject the stylesheet
  const link = document.createElement('link');
  link.id = id;
  link.rel = 'stylesheet';
  link.href = href;
  document.head.appendChild(link);

  // 3. Force a re-render once the font is truly ready
  // This fixes the FOUT issue where elements render in fallback fonts
  if (typeof document.fonts?.load === 'function') {
    document.fonts.load(`700 16px "${fontFamily}"`).then(() => {
      // Trigger a subtle reflow so React picks up the font change
      document.documentElement.style.setProperty('--font-loaded-' + normalizedName, '1');
    }).catch(() => {
      // Font load failed silently — fallback fonts will be used
    });
  }
};

/**
 * Ensures all fonts used in a list are loaded.
 * @param {string[]} fontFamilies 
 */
export const loadFonts = (fontFamilies) => {
  fontFamilies.forEach(loadGoogleFont);
};

/**
 * Returns a promise that resolves when ALL document fonts are ready.
 * Use this to delay rendering profile content until fonts are loaded.
 * @returns {Promise<void>}
 */
export const waitForFonts = () => {
  if (typeof document.fonts?.ready?.then === 'function') {
    return document.fonts.ready;
  }
  return Promise.resolve();
};
